import { useToast } from 'hooks';
import { LocationOption } from 'packages/tackle-api/src/co-sell-msft/listLocations';
import { useDebouncedSearch } from 'pages/Settings/Account/components/FieldMapper/api/hooks/useDebouncedSearch';
import { getNoOptionsText } from 'pages/Settings/Account/components/FieldMapper/components/SelectFieldOptionV2/helpers';
import { useEffect, useState } from 'react';
import { useListLocations } from '../../../../hooks/useListLocations';
import { useReferralFormContext } from '../../../ReferralFormContext';
import {
  CUSTOMER_LOOKUP_ADDRESS_CITY_FIELD,
  CUSTOMER_LOOKUP_ADDRESS_COUNTRY_FIELD,
  CUSTOMER_LOOKUP_ADDRESS_REGION_FIELD,
  CUSTOMER_LOOKUP_ADDRESS_STATE_FIELD,
  CUSTOMER_LOOKUP_COMPANY_NAME_DATA_ID,
  CUSTOMER_LOOKUP_COMPANY_NAME_FIELD_NAME,
  CUSTOMER_LOOKUP_LOCATION_FIELD_NAME,
  CUSTOMER_LOOKUP_ADDRESS_POSTAL_CODE_FIELD,
  FORM_STEP_CONTAINER_MIN_WIDTH,
  FORM_STEP_CONTAINER_WIDTH,
  LOCATION_DATA_ID,
  LOCATION_PLACEHOLDER_TEXT,
  TOAST_CONFIG,
  TYPOGRAPHY_STYLES,
  CUSTOMER_LOOKUP_ADDRESS_ADDRESS_LINE_1_FIELD,
} from '../../constants';

// COMPONENTS
import { Banner } from '@tackle-io/platform-ui';
import { Controller } from 'react-hook-form';
import { Typography, useTheme } from 'ui';
import { Autocomplete, Box, TextField } from 'vendor/material';
import { InputLabel } from '../../../components/InputLabel';
import ControlledTextField from '../../../controlledFields/ControlledTextField';

// ASSETS
import { SearchWeb } from 'mdi-material-ui';
import { CustomerLookupInput } from 'packages/tackle-api/src/co-sell-msft/customerLookup';
import { CircularProgress } from 'vendor/material';

export const LOCATION_SEARCH_ERROR_MESSAGE =
  "We're sorry. A location search error ocurred. Please try again";

export default function AddCustomerSearchForm({
  defaultValues,
}: {
  defaultValues: CustomerLookupInput;
}) {
  const theme = useTheme();
  const { control, setValue, register } = useReferralFormContext();
  const { toaster } = useToast();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const isSearchRequestEnabled = !!searchQuery && searchQuery.length > 2;

  const { isLoading, data, error, isError } = useListLocations({
    searchQuery: searchQuery,
    enabled: isSearchRequestEnabled,
  });

  const showLoadingIcon = isSearchRequestEnabled && isLoading;

  const debouncedSearch = useDebouncedSearch((value: string) => {
    setSearchQuery(value);
  }, 300);

  const options = data?.locations || [];

  useEffect(() => {
    if (isError) {
      toaster({
        message: LOCATION_SEARCH_ERROR_MESSAGE,
        options: {
          ...TOAST_CONFIG,
          type: 'error',
        },
      });
      reportError(error);
    }
  }, [isError, error, toaster]);

  return (
    <Box
      minWidth={FORM_STEP_CONTAINER_MIN_WIDTH}
      width={FORM_STEP_CONTAINER_WIDTH}
    >
      <Box mt={2}>
        <Banner
          borderPosition="top"
          title="Increase Microsoft acceptance rates by selecting a Microsoft managed account."
          type="info"
        />
      </Box>

      <Box mt={4}>
        <Typography style={{ ...TYPOGRAPHY_STYLES, fontWeight: 600 }}>
          Customer search
        </Typography>

        <Box mt={3}>
          <Controller
            control={control}
            name={CUSTOMER_LOOKUP_LOCATION_FIELD_NAME}
            render={({
              value,
              onChange,
            }: {
              value: LocationOption;
              onChange: (value: LocationOption) => void;
            }) => {
              return (
                <>
                  <Autocomplete
                    key={`add_customer_${CUSTOMER_LOOKUP_LOCATION_FIELD_NAME}`}
                    id={`add_customer_${CUSTOMER_LOOKUP_LOCATION_FIELD_NAME}`}
                    placeholder={LOCATION_PLACEHOLDER_TEXT}
                    options={options || []}
                    value={value}
                    getOptionLabel={(option: LocationOption) =>
                      option.display || ''
                    }
                    getOptionSelected={(option, value) => {
                      return option?.place_id === value?.place_id;
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          data-testid={
                            'msft_co_sell_add_customer_location_field'
                          }
                          id={'select-field-option-v2-input'}
                          placeholder={LOCATION_PLACEHOLDER_TEXT}
                          variant="outlined"
                          size="small"
                          fullWidth
                          label={
                            <InputLabel
                              dataId={LOCATION_DATA_ID}
                              label={'Location'}
                              required={true}
                            />
                          }
                          inputProps={{
                            ...params?.inputProps,
                          }}
                          InputProps={{
                            ...params?.InputProps,
                            style: {
                              fontSize: theme.typography.pxToRem(14),
                              fontWeight: 600,
                            },
                            startAdornment: <SearchWeb />,
                            endAdornment: (
                              <>
                                {showLoadingIcon ? (
                                  <CircularProgress
                                    color="inherit"
                                    size={16}
                                    style={{
                                      marginRight: theme.spacing(0),
                                    }}
                                  />
                                ) : (
                                  <>{params?.InputProps?.endAdornment}</>
                                )}
                              </>
                            ),
                          }}
                          helperText={
                            isError
                              ? "We're sorry. An location search error ocurred. Please try again"
                              : ''
                          }
                        />
                      );
                    }}
                    onChange={(_, newValue: LocationOption) => {
                      if (newValue) {
                        onChange(newValue);
                        setValue(
                          CUSTOMER_LOOKUP_ADDRESS_COUNTRY_FIELD,
                          newValue?.countryCode || '',
                        );
                        setValue(
                          CUSTOMER_LOOKUP_ADDRESS_STATE_FIELD,
                          newValue?.state || '',
                        );
                        setValue(
                          CUSTOMER_LOOKUP_ADDRESS_CITY_FIELD,
                          newValue?.city || '',
                        );
                        setValue(
                          CUSTOMER_LOOKUP_ADDRESS_POSTAL_CODE_FIELD,
                          newValue?.postalCode || '',
                        );
                        setValue(
                          CUSTOMER_LOOKUP_ADDRESS_ADDRESS_LINE_1_FIELD,
                          newValue?.addressLine1 || '',
                        );

                        return;
                      } else {
                        onChange(null);

                        return;
                      }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      // Only trigger search on user input, not on selection
                      if (reason === 'input') {
                        if (newInputValue === '') {
                          setSearchQuery('');
                        } else {
                          debouncedSearch(newInputValue);
                        }
                      }
                    }}
                    noOptionsText={getNoOptionsText({
                      isLoading,
                      searchInput: searchQuery,
                    })}
                    fullWidth
                    handleHomeEndKeys={true}
                  />
                  <input
                    id={CUSTOMER_LOOKUP_ADDRESS_COUNTRY_FIELD}
                    name={CUSTOMER_LOOKUP_ADDRESS_COUNTRY_FIELD}
                    style={{ display: 'none' }}
                    type="hidden"
                    ref={register()}
                    value={value?.countryCode}
                  />
                  <input
                    id={CUSTOMER_LOOKUP_ADDRESS_STATE_FIELD}
                    name={CUSTOMER_LOOKUP_ADDRESS_STATE_FIELD}
                    style={{ display: 'none' }}
                    type="hidden"
                    ref={register()}
                    value={value?.state}
                  />
                  <input
                    id={CUSTOMER_LOOKUP_ADDRESS_CITY_FIELD}
                    name={CUSTOMER_LOOKUP_ADDRESS_CITY_FIELD}
                    style={{ display: 'none' }}
                    type="hidden"
                    ref={register()}
                    value={value?.city}
                  />
                  <input
                    id={CUSTOMER_LOOKUP_ADDRESS_REGION_FIELD}
                    name={CUSTOMER_LOOKUP_ADDRESS_REGION_FIELD}
                    style={{ display: 'none' }}
                    type="hidden"
                    ref={register()}
                    value={value?.region}
                  />
                  <input
                    id={CUSTOMER_LOOKUP_ADDRESS_ADDRESS_LINE_1_FIELD}
                    name={CUSTOMER_LOOKUP_ADDRESS_ADDRESS_LINE_1_FIELD}
                    style={{ display: 'none' }}
                    type="hidden"
                    ref={register()}
                    value={value?.addressLine1}
                  />
                </>
              );
            }}
          />
        </Box>

        <Box mt={3}>
          <ControlledTextField
            name={CUSTOMER_LOOKUP_COMPANY_NAME_FIELD_NAME}
            control={control}
            defaultValue={defaultValues?.companyName}
            label={'Company name or Microsoft account ID'}
            helperText={`Exact name or ID will result in more precise matches. Microsoft account ID formats vary. For example: 1+XXXX+XXXX or 9-XXXXXXXXX.`}
            required={true}
            dataId={CUSTOMER_LOOKUP_COMPANY_NAME_DATA_ID}
          />
        </Box>
      </Box>
    </Box>
  );
}
